import React, { useEffect, useState } from "react";
import {
    TagPicker,
    TagPickerList,
    TagPickerInput,
    TagPickerControl,
    TagPickerOption,
    TagPickerGroup,
    Tag,
} from "@fluentui/react-components";
import styles from "./employeesField.module.scss";
import { useStore } from "../../stores/Stores";
import { SearchEmployeesResponse } from "../../customerMeetings/service/SearchEmployeesResponse";
import { useTranslation } from "../../shared/translation/Translation";

export interface EmployeesFieldProps {
    placeholder: string | undefined;
    iconComponent?: React.ReactNode;
    emails: string[];
    handleDataChange: (emails: string[]) => void;
    disabled?: boolean | undefined;
}

// Sentinel values used for non-selectable hint/status rows
const HINT_VALUE = "__hint__";
const LOADING_VALUE = "__loading__";
const NO_RESULTS_VALUE = "__no_results__";
const SENTINEL_VALUES = new Set([HINT_VALUE, LOADING_VALUE, NO_RESULTS_VALUE]);

const EmployeesField: React.FC<EmployeesFieldProps> = (props: EmployeesFieldProps) => {
    const { searchStore } = useStore();
    const messages = useTranslation();

    const [searchQuery, setSearchQuery] = useState<string>("");
    const [queryResults, setQueryResults] = useState<SearchEmployeesResponse>({ employees: [], hasMore: false });
    const [loadingData, setLoadingData] = useState(false);

    useEffect(() => {
        if (searchQuery.length < 3) {
            setQueryResults({ employees: [], hasMore: false });
            return;
        }

        const timeoutId = setTimeout(() => fetchQuery(), 400);
        return () => clearTimeout(timeoutId);
    }, [searchQuery]);

    const fetchQuery = async () => {
        setLoadingData(true);
        const result = await searchStore.searchEmployees(searchQuery);
        setQueryResults(result);
        setLoadingData(false);
    };

    const renderOptions = () => {
        if (searchQuery.length < 3) {
            return <TagPickerOption value={HINT_VALUE}>{messages.peoplePicker.enterCharacters}</TagPickerOption>;
        }
        if (loadingData) {
            return <TagPickerOption value={LOADING_VALUE}>{messages.peoplePicker.loading}</TagPickerOption>;
        }
        if (queryResults.employees.length === 0) {
            return <TagPickerOption value={NO_RESULTS_VALUE}>{messages.peoplePicker.noResults}</TagPickerOption>;
        }
        return (
            <>
                {queryResults.employees.map((e) => (
                    <TagPickerOption key={e.email} value={e.email}>
                        {e.email}
                    </TagPickerOption>
                ))}
                {queryResults.hasMore && (
                    <TagPickerOption value="__more__" style={{ fontWeight: 700 }}>
                        {messages.peoplePicker.moreResults}
                    </TagPickerOption>
                )}
            </>
        );
    };

    const handleOptionSelect = (_: unknown, data: { selectedOptions: string[] }) => {
        const validEmails = data.selectedOptions.filter((v) => !SENTINEL_VALUES.has(v));
        props.handleDataChange(validEmails.slice());
        setSearchQuery("");
    };

    return (
        <div className={styles.textFieldWrapper}>
            <div className={styles.iconComponent}>{props.iconComponent}</div>
            <TagPicker onOptionSelect={handleOptionSelect} selectedOptions={props.emails} disabled={props.disabled}>
                <TagPickerControl
                    className={props.disabled ? styles.dropDownDisabled : styles.dropDown}
                    style={{ width: "100%" }}
                >
                    <TagPickerGroup>
                        {props.emails.map((email) => (
                            <Tag
                                key={email}
                                value={email}
                                dismissible={!props.disabled}
                                dismissIcon={{ "aria-label": "remove" }}
                            >
                                {email}
                            </Tag>
                        ))}
                    </TagPickerGroup>
                    <TagPickerInput
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onBlur={() => setSearchQuery("")}
                        placeholder={props.emails.length === 0 ? props.placeholder : undefined}
                    />
                </TagPickerControl>
                <TagPickerList>{renderOptions()}</TagPickerList>
            </TagPicker>
        </div>
    );
};

export default EmployeesField;
